import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { artworks } from '../../mocks/artworks';

export default function Gallery() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArtwork, setSelectedArtwork] = useState<typeof artworks[0] | null>(null);

  const categories = ['all', 'abstract', 'portrait', 'landscape', 'mixed media'];

  const filteredArtworks = selectedCategory === 'all' 
    ? artworks 
    : artworks.filter(art => art.category === selectedCategory);

  // Ordenar por año (más reciente primero)
  const sortedArtworks = [...filteredArtworks].sort((a, b) => {
    return parseInt(b.year) - parseInt(a.year);
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/955e5012e0c08831cdef33aaca24fd4b.jpg"
            alt="Gallery"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Galería de Arte</h1>
          <p className="text-xl md:text-2xl font-light">Explora la colección completa de obras</p>
        </div>
      </div>

      {/* Filter Section */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                selectedCategory === category
                  ? 'bg-[#e8a65b] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category === 'all' ? 'Todas' : category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Gallery Grid - Preparado para Shopify */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
          {sortedArtworks.map((artwork) => (
            <div
              key={artwork.id}
              onClick={() => setSelectedArtwork(artwork)}
              className="group cursor-pointer"
            >
              <div className="relative overflow-hidden rounded-lg bg-gray-100 aspect-square mb-4">
                <img
                  src={artwork.image}
                  alt={artwork.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"></div>
                {artwork.sold ? (
                  <div className="absolute top-4 right-4 bg-[#e8a65b] text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    VENDIDO
                  </div>
                ) : (
                  <div className="absolute top-4 right-4 bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg animate-pulse">
                    DISPONIBLE
                  </div>
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1">{artwork.title}</h3>
              <p className="text-sm text-gray-500 mb-2">{artwork.year}</p>
              <p className="text-lg font-medium text-[#e8a65b]">${artwork.price.toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Artwork Modal */}
      {selectedArtwork && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedArtwork(null)}
        >
          <div 
            className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedArtwork.sold && (
              <div className="absolute top-8 left-8 bg-[#e8a65b] text-white px-6 py-3 rounded-full text-base font-bold shadow-lg z-10">
                VENDIDO
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-8 p-8">
              <div className="w-full h-[400px] md:h-[500px] flex items-center justify-center bg-gray-50 rounded-lg">
                <img
                  src={selectedArtwork.image}
                  alt={selectedArtwork.title}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex flex-col justify-center">
                <span className="text-sm text-gray-500 uppercase tracking-wider mb-2">
                  {selectedArtwork.category}
                </span>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">{selectedArtwork.title}</h2>
                <p className="text-lg text-gray-600 mb-6">{selectedArtwork.year}</p>
                <p className="text-gray-700 mb-6 leading-relaxed">{selectedArtwork.description}</p>
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-1">Dimensiones</p>
                  <p className="text-lg text-gray-900">{selectedArtwork.dimensions}</p>
                </div>
                <div className="mb-8">
                  <p className="text-sm text-gray-500 mb-1">Técnica</p>
                  <p className="text-lg text-gray-900">{selectedArtwork.medium}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-3xl font-bold text-[#e8a65b]">
                    ${selectedArtwork.price.toLocaleString()}
                  </p>
                  {!selectedArtwork.sold && (
                    <button className="bg-[#e8a65b] text-white px-8 py-3 rounded-full font-medium hover:bg-[#d99549] transition-colors whitespace-nowrap cursor-pointer">
                      Añadir al Carrito
                    </button>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => setSelectedArtwork(null)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white rounded-full text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
